import React, { useEffect, useMemo, useState } from "react";
import { Navigate } from "react-router-dom";
import { useAuth, effectiveTier } from "@/lib/AuthContext";
import DashboardShell from "@/components/layout/SmartShell";
import CreditCostStrip from "@/components/credits/CreditCostStrip";
import SmartExportModal from "@/components/exports/SmartExportModal";
import api, { formatApiErrorDetail, BACKEND_URL } from "@/lib/api";
import { Loader2, Download, Sparkles, Share2, Wand2, RotateCcw, ChevronDown, ChevronUp } from "lucide-react";

/* ── Option catalogs tuned for ad / product / promo image generation ── */
const SUBJECT_TYPES = [
  { id: "product",     label: "Product / SKU" },
  { id: "brand",       label: "Brand / Logo composition" },
  { id: "model",       label: "Model / Talent" },
  { id: "food",        label: "Food / Beverage" },
  { id: "fashion",     label: "Fashion / Apparel" },
  { id: "real_estate", label: "Real-estate / Property" },
  { id: "scene",       label: "Cinematic scene" },
  { id: "lifestyle",   label: "Lifestyle moment" },
];

const USE_CASES = [
  { id: "hero",          label: "Hero / Ad creative" },
  { id: "feed",          label: "Social feed" },
  { id: "story",         label: "Story / Reel still" },
  { id: "thumbnail",     label: "Thumbnail" },
  { id: "product_shot",  label: "Product shot" },
  { id: "brand_visual",  label: "Brand visual" },
];

const STYLES = [
  { id: "modern",        label: "Modern editorial" },
  { id: "studio",        label: "Studio softbox" },
  { id: "lifestyle",     label: "Lifestyle / candid" },
  { id: "luxury",        label: "Luxury / glossy" },
  { id: "vintage_film",  label: "Vintage film" },
  { id: "anime",         label: "Anime / illustrated" },
  { id: "noir",          label: "Noir / high-contrast" },
  { id: "minimal",       label: "Minimal flat-lay" },
];

const MOODS = [
  { id: "cinematic",  label: "Cinematic" },
  { id: "warm",       label: "Warm / inviting" },
  { id: "moody",      label: "Moody / dramatic" },
  { id: "premium",    label: "Premium / clean" },
  { id: "bold",       label: "Bold / vivid" },
  { id: "soft",       label: "Soft / minimal" },
];

const COMPOSITIONS = [
  { id: "centered",         label: "Centered" },
  { id: "rule_of_thirds",   label: "Rule of thirds" },
  { id: "close_up",         label: "Close-up / macro" },
  { id: "wide_shot",        label: "Wide / environmental" },
  { id: "flat_lay",         label: "Flat-lay / top-down" },
  { id: "low_angle",        label: "Low angle / hero" },
];

const BACKGROUNDS = [
  { id: "studio_neutral",   label: "Neutral studio" },
  { id: "marble",           label: "Marble surface" },
  { id: "urban",            label: "Urban / city" },
  { id: "nature",           label: "Nature / outdoor" },
  { id: "interior",         label: "Luxury interior" },
  { id: "abstract_gradient",label: "Abstract gradient" },
  { id: "transparent",      label: "Solid / pure background" },
];

const LIGHTING = [
  { id: "soft_box",     label: "Soft box studio" },
  { id: "golden_hour",  label: "Golden hour" },
  { id: "neon",         label: "Neon / colored" },
  { id: "natural",      label: "Natural daylight" },
  { id: "dramatic_rim", label: "Dramatic rim light" },
  { id: "moody_low",    label: "Moody low-key" },
];

const ASPECTS = [
  { id: "1:1",  label: "Square · 1:1" },
  { id: "9:16", label: "Vertical · 9:16" },
  { id: "16:9", label: "Widescreen · 16:9" },
  { id: "4:5",  label: "Portrait · 4:5" },
];

const subjectCopy = Object.fromEntries(SUBJECT_TYPES.map(o => [o.id, o.label]));
const useCaseCopy = Object.fromEntries(USE_CASES.map(o => [o.id, o.label]));
const styleCopy   = Object.fromEntries(STYLES.map(o => [o.id, o.label]));
const moodCopy    = Object.fromEntries(MOODS.map(o => [o.id, o.label]));
const compCopy    = Object.fromEntries(COMPOSITIONS.map(o => [o.id, o.label]));
const bgCopy      = Object.fromEntries(BACKGROUNDS.map(o => [o.id, o.label]));
const lightCopy   = Object.fromEntries(LIGHTING.map(o => [o.id, o.label]));

function buildImagePrompt(brief) {
  const subject = (brief.subject || "").trim();
  if (!subject) return "";
  const parts = [];

  const uc = useCaseCopy[brief.useCase] || "ad creative";
  parts.push(`${uc.toLowerCase()} of ${subject}`);

  parts.push(`${(styleCopy[brief.style] || "modern editorial").toLowerCase()} style`);
  parts.push(`${(moodCopy[brief.mood]   || "cinematic").toLowerCase()} mood`);
  parts.push(`${(compCopy[brief.composition] || "centered").toLowerCase()} composition`);
  parts.push(`${(bgCopy[brief.background]    || "neutral studio").toLowerCase()} background`);
  parts.push(`${(lightCopy[brief.lighting]   || "soft box studio").toLowerCase()} lighting`);

  if (brief.brandColors?.trim())  parts.push(`brand colors: ${brief.brandColors.trim()}`);
  if (brief.includeText?.trim())  parts.push(`include legible text reading "${brief.includeText.trim()}"`);
  if (brief.mustInclude?.trim())  parts.push(`must include: ${brief.mustInclude.trim()}`);

  parts.push("sharp focus, tactile materials, no watermarks, photo-real commercial advertising quality");

  const neg = (brief.negativePrompt || "").trim();
  const base = parts.join(", ");
  return neg ? `${base}. Avoid: ${neg}.` : `${base}.`;
}

export default function ImageGeneratePage() {
  const { user, loading, refresh } = useAuth();
  const [brief, setBrief] = useState({
    subject:        "",
    subjectType:    "product",
    useCase:        "hero",
    style:          "modern",
    mood:           "cinematic",
    composition:    "centered",
    background:     "studio_neutral",
    lighting:       "soft_box",
    aspect:         "1:1",
    brandColors:    "",
    includeText:    "",
    mustInclude:    "",
    negativePrompt: "",
    advancedPrompt: "",
  });
  const [advancedOpen, setAdvancedOpen] = useState(false);
  const [busy,    setBusy]    = useState(false);
  const [error,   setError]   = useState("");
  const [result,  setResult]  = useState(null);
  const [exportOpen, setExportOpen] = useState(false);

  useEffect(() => {
    try {
      const raw = sessionStorage.getItem("ybm_autofill_prompt");
      if (raw) {
        const p = JSON.parse(raw);
        if (p?.kind === "image") {
          setBrief((b) => ({
            ...b,
            subject: p.subject || p.prompt?.slice(0, 80) || "",
            aspect:  p.aspect || b.aspect,
            advancedPrompt: p.prompt || "",
          }));
          if (p.prompt) setAdvancedOpen(true);
          sessionStorage.removeItem("ybm_autofill_prompt");
        }
      }
    } catch {}
    try {
      const stored = sessionStorage.getItem("ybm_reuse_image_brief");
      if (stored) {
        setBrief((b) => ({ ...b, ...JSON.parse(stored) }));
        sessionStorage.removeItem("ybm_reuse_image_brief");
      }
    } catch {}
    try {
      const q = new URLSearchParams(window.location.search);
      const pq = q.get("prompt");
      if (pq) {
        setBrief((b) => ({ ...b, subject: pq.slice(0, 80), advancedPrompt: pq }));
        setAdvancedOpen(true);
      }
    } catch {}
  }, []);

  const assembledPrompt = useMemo(() => {
    const adv = brief.advancedPrompt?.trim();
    return adv ? adv : buildImagePrompt(brief);
  }, [brief]);

  if (loading || user === undefined) return null;
  if (!user) return <Navigate to="/login" replace />;
  const tier = effectiveTier(user);

  const onGenerate = async () => {
    setError(""); setResult(null);
    if (!assembledPrompt || assembledPrompt.length < 6) {
      setError("Tell us what you want to make — fill in the subject above.");
      return;
    }
    setBusy(true);
    try {
      const { data } = await api.post("/generate/image", {
        prompt: assembledPrompt,
        style:  brief.useCase === "product_shot" ? "product" : "commercial",
        aspect: brief.aspect,
      });
      setResult(data);
      await refresh();
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setBusy(false);
    }
  };

  const onReuseBrief = () => {
    setResult(null); setError(""); setExportOpen(false);
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const downloadHref = result ? `${BACKEND_URL}${result.download_url}` : null;

  return (
    <DashboardShell>
      <div className="max-w-4xl mx-auto px-4 py-8 sm:py-10" data-testid="image-generator-page">
        <div className="mb-7">
          <div className="inline-flex items-center gap-2 text-[10px] uppercase tracking-[0.22em] text-[#D4AF37] mb-3">
            <Sparkles className="w-3.5 h-3.5" /> Guided · Image Generator
          </div>
          <h1 className="font-display text-3xl sm:text-4xl font-bold text-[#F4F4F0]">Generate an image</h1>
          <p className="text-[#A0A0A0] text-sm mt-2 max-w-2xl">
            Built for ads, product shots, brand visuals and social creative. Fill the brief and we'll assemble a commercially-tuned prompt for Gemini Nano Banana.
            {tier === "premium" && <span className="text-[#D4AF37] ml-2">· You're on Premium.</span>}
          </p>
        </div>

        {!busy && !result && (
          <>
            <div className="space-y-5 border border-[#1F1F1F] bg-[#0A0A0A] p-5 sm:p-6">
              <Field label="What do you want to create?" required>
                <input
                  data-testid="image-subject"
                  value={brief.subject}
                  onChange={(e) => setBrief((b) => ({ ...b, subject: e.target.value }))}
                  placeholder="e.g. matte black coffee bag on marble · gold lipstick tube · model wearing all-black streetwear"
                  className="w-full bg-[#121212] border border-[#2A2A2A] focus:border-[#D4AF37] outline-none px-3 py-3 text-sm text-[#F4F4F0]"
                />
              </Field>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <Selector label="Subject type"   value={brief.subjectType} options={SUBJECT_TYPES} onChange={(v) => setBrief((b) => ({ ...b, subjectType: v }))} testId="image-subject-type" />
                <Selector label="Use case"       value={brief.useCase}     options={USE_CASES}     onChange={(v) => setBrief((b) => ({ ...b, useCase: v }))}     testId="image-use-case" />
                <Selector label="Style"          value={brief.style}       options={STYLES}        onChange={(v) => setBrief((b) => ({ ...b, style: v }))}       testId="image-style" />
                <Selector label="Mood"           value={brief.mood}        options={MOODS}         onChange={(v) => setBrief((b) => ({ ...b, mood: v }))}        testId="image-mood" />
                <Selector label="Composition"    value={brief.composition} options={COMPOSITIONS}  onChange={(v) => setBrief((b) => ({ ...b, composition: v }))} testId="image-composition" />
                <Selector label="Background"     value={brief.background}  options={BACKGROUNDS}   onChange={(v) => setBrief((b) => ({ ...b, background: v }))}  testId="image-background" />
                <Selector label="Lighting"       value={brief.lighting}    options={LIGHTING}      onChange={(v) => setBrief((b) => ({ ...b, lighting: v }))}    testId="image-lighting" />
                <Selector label="Aspect"         value={brief.aspect}      options={ASPECTS}       onChange={(v) => setBrief((b) => ({ ...b, aspect: v }))}      testId="image-aspect" />
              </div>

              <Field label="Brand colors (optional)">
                <input
                  data-testid="image-brand-colors"
                  value={brief.brandColors}
                  onChange={(e) => setBrief((b) => ({ ...b, brandColors: e.target.value }))}
                  placeholder="e.g. #D4AF37 gold and #080808 black · or 'cream and burgundy'"
                  className="w-full bg-[#121212] border border-[#2A2A2A] focus:border-[#D4AF37] outline-none px-3 py-3 text-sm text-[#F4F4F0]"
                />
              </Field>

              <Field label="Text to include (optional)">
                <input
                  data-testid="image-include-text"
                  value={brief.includeText}
                  onChange={(e) => setBrief((b) => ({ ...b, includeText: e.target.value }))}
                  placeholder='e.g. "Velvet" album name · "Limited Edition" sticker'
                  className="w-full bg-[#121212] border border-[#2A2A2A] focus:border-[#D4AF37] outline-none px-3 py-3 text-sm text-[#F4F4F0]"
                />
              </Field>

              <Field label="Must-include details (optional)">
                <textarea
                  data-testid="image-must-include"
                  rows={2}
                  value={brief.mustInclude}
                  onChange={(e) => setBrief((b) => ({ ...b, mustInclude: e.target.value }))}
                  placeholder="e.g. visible logo on the side · droplets of condensation · gold foil accents"
                  className="w-full bg-[#121212] border border-[#2A2A2A] focus:border-[#D4AF37] outline-none px-3 py-3 text-sm text-[#F4F4F0] resize-none"
                />
              </Field>

              <Field label="Avoid (optional)">
                <input
                  data-testid="image-negative"
                  value={brief.negativePrompt}
                  onChange={(e) => setBrief((b) => ({ ...b, negativePrompt: e.target.value }))}
                  placeholder="e.g. no people · no text overlays · no plastic shine"
                  className="w-full bg-[#121212] border border-[#2A2A2A] focus:border-[#D4AF37] outline-none px-3 py-3 text-sm text-[#F4F4F0]"
                />
              </Field>

              <div className="border-t border-[#1F1F1F] pt-4">
                <button
                  type="button"
                  onClick={() => setAdvancedOpen((v) => !v)}
                  data-testid="image-advanced-toggle"
                  className="text-[10px] uppercase tracking-[0.22em] text-[#A0A0A0] hover:text-[#D4AF37] flex items-center gap-1.5"
                >
                  {advancedOpen ? <ChevronUp className="w-3 h-3" /> : <ChevronDown className="w-3 h-3" />}
                  Advanced · Use my own prompt
                </button>
                {advancedOpen && (
                  <textarea
                    data-testid="image-advanced-prompt"
                    rows={4}
                    value={brief.advancedPrompt}
                    onChange={(e) => setBrief((b) => ({ ...b, advancedPrompt: e.target.value }))}
                    placeholder="Override the guided brief with a full custom prompt. Leave empty to use the structured fields above."
                    className="mt-3 w-full bg-[#121212] border border-[#2A2A2A] focus:border-[#D4AF37] outline-none px-3 py-2.5 text-sm text-[#F4F4F0] resize-none font-mono"
                  />
                )}
              </div>
            </div>

            {assembledPrompt && (
              <div className="mt-4 px-4 py-3 border border-[#1F1F1F] bg-[#080808]" data-testid="image-prompt-preview">
                <p className="text-[10px] uppercase tracking-[0.22em] text-[#5A5A5A] mb-1">Prompt we'll send</p>
                <p className="text-xs text-[#A0A0A0] leading-relaxed">{assembledPrompt}</p>
              </div>
            )}

            <CreditCostStrip feature="image" quality="pro" label="Pro · 1 image" />

            {error && <div className="mb-4 px-4 py-3 border border-red-500/40 bg-red-500/10 text-red-300 text-sm" data-testid="image-gen-error">{error}</div>}

            <button
              onClick={onGenerate}
              disabled={busy}
              data-testid="image-generate-button"
              className="mt-2 w-full inline-flex items-center justify-center gap-2 bg-[#D4AF37] hover:bg-[#F0C84B] text-[#080808] font-bold py-3.5 text-sm uppercase tracking-wider transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {busy
                ? <><Loader2 className="w-4 h-4 animate-spin" /> Rendering image…</>
                : <><Wand2 className="w-4 h-4" /> Generate Image</>}
            </button>
            <p className="text-[10px] text-[#5A5A5A] uppercase tracking-wider mt-3 text-center">
              Every render debits credits from your wallet · Failed renders auto-refund
            </p>
          </>
        )}

        {busy && (
          <div className="border border-[#1F1F1F] bg-[#0A0A0A] min-h-[420px] flex items-center justify-center p-6 mt-4" data-testid="image-loading-state">
            <div className="text-center">
              <Loader2 className="w-10 h-10 text-[#D4AF37] animate-spin mx-auto mb-4" />
              <p className="text-sm text-[#F4F4F0] font-semibold">Generating your image…</p>
              <p className="text-xs text-[#A0A0A0] mt-2">Typically 5–15 seconds.</p>
            </div>
          </div>
        )}

        {!busy && result && (
          <div className="space-y-5" data-testid="image-result">
            <div className="bg-black flex items-center justify-center">
              <img
                src={`${BACKEND_URL}${result.preview_url}`}
                alt="Generated"
                className="max-w-full max-h-[70vh] object-contain"
              />
            </div>
            <div className="flex flex-col sm:flex-row gap-3 justify-between items-start sm:items-center">
              <span className="text-xs text-[#A0A0A0]">
                {result.watermarked ? "Watermarked (free trial)" : "No watermark"} · {result.tier_at_render}
              </span>
              <div className="flex flex-wrap gap-2">
                <button
                  onClick={onReuseBrief}
                  data-testid="image-reuse-brief"
                  className="inline-flex items-center gap-2 border border-[#2A2A2A] hover:border-[#D4AF37] text-[#F4F4F0] px-4 py-2.5 text-sm transition-colors"
                >
                  <RotateCcw className="w-3.5 h-3.5" /> Reuse brief
                </button>
                <button
                  onClick={() => setExportOpen(true)}
                  data-testid="image-smart-export"
                  className="inline-flex items-center gap-2 border border-[#D4AF37]/50 hover:border-[#D4AF37] text-[#D4AF37] px-4 py-2.5 text-sm transition-colors"
                >
                  <Share2 className="w-3.5 h-3.5" /> Smart Export
                </button>
                <a
                  href={downloadHref}
                  data-testid="image-download-button"
                  className="inline-flex items-center gap-2 border border-[#2A2A2A] hover:border-[#D4AF37] text-[#F4F4F0] px-4 py-2.5 text-sm transition-colors"
                >
                  <Download className="w-3.5 h-3.5" /> Download PNG
                </a>
              </div>
            </div>
          </div>
        )}

        <SmartExportModal
          open={exportOpen}
          onClose={() => setExportOpen(false)}
          renderId={result?.render_id}
          sourceAspect={brief.aspect}
          sourceKind="image"
        />
      </div>
    </DashboardShell>
  );
}

function Field({ label, required, children }) {
  return (
    <label className="block">
      <span className="block text-[10px] uppercase tracking-wider text-[#A0A0A0] mb-2">
        {label} {required && <span className="text-[#D4AF37]">*</span>}
      </span>
      {children}
    </label>
  );
}

function Selector({ label, value, options, onChange, testId }) {
  return (
    <Field label={label}>
      <select
        data-testid={testId}
        value={value}
        onChange={(e) => onChange(typeof options[0].id === "number" ? Number(e.target.value) : e.target.value)}
        className="w-full bg-[#121212] border border-[#2A2A2A] focus:border-[#D4AF37] outline-none px-3 py-3 text-sm text-[#F4F4F0]"
      >
        {options.map((o) => <option key={o.id} value={o.id}>{o.label}</option>)}
      </select>
    </Field>
  );
}
